"use client";

import { useAnalytics, useMarketingAnalytics } from "@analytics";
import { useAnalyticsCaptureReady } from "@analytics/lib/analytics-capture-ready-context";
import {
	URL_CHANGE_EVENT,
	type UrlChangeDetail,
} from "@shared/lib/history/dispatch-url-change";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef } from "react";

/**
 * Debounced `$pageview` + `landing_section_viewed` when the landing hash is updated via `replaceStateAndNotify`.
 */
export function AnalyticsRouteListener() {
	const pathname = usePathname();
	const captureReady = useAnalyticsCaptureReady();
	const { capturePageView } = useAnalytics();
	const { landingSectionViewed } = useMarketingAnalytics();
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const scheduleFlush = useCallback(
		(detail?: UrlChangeDetail) => {
			if (typeof window === "undefined" || !captureReady) {
				return;
			}
			if (timerRef.current != null) {
				clearTimeout(timerRef.current);
			}
			timerRef.current = setTimeout(() => {
				const url = window.location.href;
				if (detail?.source !== "marketing-landing-section") {
					capturePageView({ url });
				}
				if (detail?.source === "marketing-landing-section") {
					const sectionId = window.location.hash.slice(1);
					if (sectionId !== "") {
						landingSectionViewed({
							section_id: sectionId,
						});
					}
				}
				timerRef.current = null;
			}, 250);
		},
		[capturePageView, captureReady, landingSectionViewed],
	);

	useEffect(() => {
		if (!captureReady) {
			return;
		}
		scheduleFlush();
	}, [captureReady, pathname, scheduleFlush]);

	useEffect(() => {
		const onHash = () => {
			scheduleFlush();
		};
		const onPop = () => {
			scheduleFlush();
		};
		const onSqUrl = (event: Event) => {
			if (!(event instanceof CustomEvent)) {
				return;
			}
			scheduleFlush(event.detail as UrlChangeDetail);
		};

		window.addEventListener("hashchange", onHash);
		window.addEventListener("popstate", onPop);
		window.addEventListener(URL_CHANGE_EVENT, onSqUrl);

		return () => {
			window.removeEventListener("hashchange", onHash);
			window.removeEventListener("popstate", onPop);
			window.removeEventListener(URL_CHANGE_EVENT, onSqUrl);
			if (timerRef.current != null) {
				clearTimeout(timerRef.current);
			}
		};
	}, [scheduleFlush]);

	return null;
}
